import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';

/**
 * Shared header shell for the /help/docs and /help/guides pages.
 * Renders a Stripe-style search bar (with ⌘K keyboard hint + shortcut)
 * plus a category grid that scrolls to anchors in the article list
 * rendered by the parent page. The parent owns article rendering — this
 * component just publishes `search` / `activeCategoryId` state via the
 * `onSearchChange` / `onCategoryClick` callbacks (or via the `render`
 * function pattern below for tighter coupling).
 *
 * Categories should already have `article_count` computed by the parent
 * (post any grade filter, so counts reflect what the user will actually
 * see). Categories with 0 visible articles are hidden.
 *
 * `onCategoryClick(id)` — fires when a card is clicked; parent should
 * scroll to `#cat-{id}` in its article list (or `#uncategorised` when
 * id is `null`).
 */
const HelpBrowseShell = ({
  title,
  subtitle,
  categories = [], // [{id, name, article_count}]
  uncategorisedCount = 0,
  search,
  onSearchChange,
  resultsCount = null, // if !== null, show "N results for '…'"
  onCategoryClick,
  headerRight = null, // e.g. grade-filter chip for Guides
}) => {
  const inputRef = useRef(null);

  // ⌘K / Ctrl+K to focus the search bar — cheap power-user delight.
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const visibleCats = useMemo(
    () => categories.filter(c => (c.article_count ?? 0) > 0),
    [categories]
  );

  return (
    <div className="mb-8" data-testid="help-browse-shell">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" data-testid="help-page-title">
            {title}
          </h1>
          {subtitle && (
            <p className="text-slate-600 mt-1 text-sm sm:text-base">{subtitle}</p>
          )}
        </div>
        {headerRight}
      </div>

      {/* Search — Stripe-style. Focus ring, ⌘K hint, clear-X button. */}
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
          <Search className="w-4 h-4" />
        </div>
        <Input
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search articles…"
          className="pl-10 pr-24 h-12 text-base bg-white border-slate-200 focus-visible:ring-[#0A7EA4]/40"
          data-testid="help-search-input"
        />
        {search ? (
          <button
            type="button"
            onClick={() => onSearchChange('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition"
            data-testid="help-search-clear"
            aria-label="Clear search"
          >
            <X className="w-4 h-4" />
          </button>
        ) : (
          <div
            className="absolute right-3 top-1/2 -translate-y-1/2 hidden sm:flex items-center gap-1 text-xs text-slate-400 pointer-events-none"
            aria-hidden="true"
          >
            <kbd className="px-1.5 py-0.5 border border-slate-200 rounded bg-slate-50 font-mono text-[10px]">⌘</kbd>
            <kbd className="px-1.5 py-0.5 border border-slate-200 rounded bg-slate-50 font-mono text-[10px]">K</kbd>
          </div>
        )}
      </div>

      {resultsCount !== null && (
        <p className="text-xs text-slate-500 mt-2" data-testid="help-search-results-count">
          {resultsCount} {resultsCount === 1 ? 'result' : 'results'} for &ldquo;<span className="font-medium text-slate-700">{search}</span>&rdquo;
        </p>
      )}

      {/* Category grid — hidden while searching (would fight the filtered
          view). Scroll-to-anchor on click. */}
      {!search && (visibleCats.length > 0 || uncategorisedCount > 0) && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-6" data-testid="help-category-grid">
          {visibleCats.map(c => (
            <button
              key={c.id}
              type="button"
              onClick={() => onCategoryClick(c.id)}
              className="group text-left p-4 rounded-lg border border-slate-200 bg-white hover:border-[#0A7EA4] hover:shadow-sm transition"
              data-testid={`help-category-card-${c.id}`}
            >
              <div className="font-semibold text-slate-900 group-hover:text-[#0A7EA4] transition-colors line-clamp-1">
                {c.name}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {c.article_count} {c.article_count === 1 ? 'article' : 'articles'}
              </div>
            </button>
          ))}
          {uncategorisedCount > 0 && (
            <button
              type="button"
              onClick={() => onCategoryClick(null)}
              className="group text-left p-4 rounded-lg border border-dashed border-slate-300 bg-slate-50/50 hover:border-[#0A7EA4] hover:bg-white transition"
              data-testid="help-category-card-uncategorised"
            >
              <div className="font-semibold text-slate-700 group-hover:text-[#0A7EA4] transition-colors">
                Other
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {uncategorisedCount} {uncategorisedCount === 1 ? 'article' : 'articles'}
              </div>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default HelpBrowseShell;

/**
 * Small utility: strip HTML tags for full-text search. Cheap client-side
 * search — good enough at <100 articles. If we ever cross that we'll
 * push search into the backend.
 */
export const stripHtml = (html = '') =>
  html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/\s+/g, ' ').trim();

export const matchesSearch = (item, needle) => {
  if (!needle) return true;
  const n = needle.toLowerCase();
  const title = (item.title || '').toLowerCase();
  const summary = (item.summary || '').toLowerCase();
  const content = stripHtml(item.content || '').toLowerCase();
  return title.includes(n) || summary.includes(n) || content.includes(n);
};

/**
 * Slug a section title into a URL anchor id. Matches backend
 * `generate_slug` closely so linkable anchors stay stable across
 * renames of the same section.
 */
export const anchorId = (title) =>
  (title || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
